import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  assessmentsService,
  ScorecardNarrative,
  ScorecardRating,
  ScorecardRespondent,
  ScorecardResponse,
  ScorecardSavePayload,
} from '../api/assessmentsService';
import AutoGrowTextarea from './AutoGrowTextarea';
import { AUTOSAVE_DELAY_MS, SaveState, saveStateLabel } from '../hooks/useDebouncedAutosave';

export const scorecardQueryKey = (assessmentId: number, assignmentId: number) => [
  'assessments',
  assessmentId,
  'report-assignments',
  assignmentId,
  'scorecard',
];

export const useScorecard = (assessmentId: number, assignmentId: number) =>
  useQuery({
    queryKey: scorecardQueryKey(assessmentId, assignmentId),
    queryFn: () => assessmentsService.getScorecard(assessmentId, assignmentId),
    enabled: Number.isFinite(assessmentId) && assessmentId > 0 && Number.isFinite(assignmentId) && assignmentId > 0,
  });

const RATING_OPTIONS: Array<{ value: ScorecardRating; label: string; short: string }> = [
  { value: 1, label: 'Strongly Disagree', short: 'SD' },
  { value: 2, label: 'Disagree', short: 'D' },
  { value: 3, label: 'Agree', short: 'A' },
  { value: 4, label: 'Strongly Agree', short: 'SA' },
];

const respondentLabel: Record<ScorecardRespondent, string> = {
  candidate: 'Candidate',
  spouse: 'Spouse',
  husband: 'Husband',
  wife: 'Wife',
};

/**
 * `heading` is the wording used when this narrative is pushed into the report
 * body, by either the prefill or an Insert button, so the two cannot drift.
 */
const narrativeFields: Array<{ key: keyof ScorecardNarrative; label: string; heading: string; help: string }> = [
  {
    key: 'strengths',
    label: 'Strengths in this focus area',
    heading: 'Strengths',
    help: 'A few sentences identifying strengths.',
  },
  {
    key: 'growthAreas',
    label: 'Growth areas in this focus area',
    heading: 'Growth Areas',
    help: 'A few sentences identifying growth areas.',
  },
  {
    key: 'recommendations',
    label: 'Growth recommendations and action steps',
    heading: 'Growth Recommendations & Action Steps',
    help: 'A few sentences identifying recommendations and action steps.',
  },
];

const formatNarrativeForInsert = (entry: { heading: string; body: string }) => (
  `${entry.heading}\n${entry.body.trim()}`
);

const emptyNarrative: ScorecardNarrative = { strengths: '', growthAreas: '', recommendations: '' };

const ratingKey = (statementId: number, respondent: ScorecardRespondent) => `${statementId}:${respondent}`;

/**
 * Turns the scorecard narratives into a starting point for the written report,
 * so assessors do not retype what they already wrote on the scorecard.
 */
export const buildReportDraftFromScorecard = (summary?: ScorecardNarrative | null): string => {
  if (!summary) return '';
  const blocks = narrativeFields
    .map((field) => ({ heading: field.heading, body: summary[field.key] || '' }))
    .filter((block) => block.body.trim());

  if (!blocks.length) return '';
  return blocks.map(formatNarrativeForInsert).join('\n\n');
};

export const scorecardAverage = (statements: ScorecardResponse['statements']) => {
  const ratings = statements.flatMap((statement) =>
    Object.values(statement.ratings).filter((rating): rating is ScorecardRating => Boolean(rating)));
  if (!ratings.length) return null;
  const total = ratings.reduce((sum, rating) => sum + rating, 0);
  return Math.round((total / ratings.length) * 10) / 10;
};

const RatingRow: React.FC<{
  name: string;
  value: ScorecardRating | null;
  showLabel: boolean;
  label: string;
  disabled: boolean;
  onChange: (value: ScorecardRating | null) => void;
}> = ({ name, value, showLabel, label, disabled, onChange }) => (
  <div className="flex items-center gap-3">
    {showLabel && (
      <div className="w-16 shrink-0 text-xs font-medium text-gray-600">{label}</div>
    )}
    <div className="grid flex-1 grid-cols-4 gap-1">
      {RATING_OPTIONS.map((option) => (
        <label
          key={option.value}
          className={[
            'flex items-center justify-center rounded-md border px-2 py-2 text-center text-xs',
            value === option.value
              ? 'border-primary-500 bg-primary-50 font-medium text-primary-700'
              : 'border-gray-200 text-gray-600',
            disabled ? 'cursor-not-allowed opacity-70' : 'cursor-pointer hover:border-gray-300',
          ].join(' ')}
        >
          <input
            type="radio"
            className="sr-only"
            name={name}
            value={option.value}
            checked={value === option.value}
            disabled={disabled}
            onChange={() => onChange(option.value)}
          />
          <span className="sm:hidden">{option.short}</span>
          <span className="hidden sm:inline">{option.label}</span>
        </label>
      ))}
    </div>
    <button
      type="button"
      className="shrink-0 text-xs text-gray-400 underline underline-offset-2 hover:text-gray-600 disabled:opacity-40"
      onClick={() => onChange(null)}
      disabled={disabled || value === null || value === undefined}
    >
      Clear
    </button>
  </div>
);

export const ScorecardTab: React.FC<{
  assessmentId: number;
  assignmentId: number;
  canEdit: boolean;
  onContinueToReport?: () => void;
}> = ({ assessmentId, assignmentId, canEdit, onContinueToReport }) => {
  const { data, isLoading, isError } = useScorecard(assessmentId, assignmentId);
  const scorecardData = data?.data;
  const queryClient = useQueryClient();

  const [ratings, setRatings] = useState<Record<string, ScorecardRating | null>>({});
  const [narrative, setNarrative] = useState<ScorecardNarrative>(emptyNarrative);
  const [saveState, setSaveState] = useState<SaveState>('idle');
  const hydratedForRef = useRef<number | null>(null);
  const dirtyRatingsRef = useRef<Set<string>>(new Set());
  const dirtyNarrativeRef = useRef<Set<keyof ScorecardNarrative>>(new Set());
  const ratingsRef = useRef<Record<string, ScorecardRating | null>>({});
  const narrativeRef = useRef<ScorecardNarrative>(emptyNarrative);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  ratingsRef.current = ratings;
  narrativeRef.current = narrative;

  useEffect(() => {
    if (!scorecardData || hydratedForRef.current === assignmentId) return;
    const initialRatings: Record<string, ScorecardRating | null> = {};
    for (const statement of scorecardData.statements) {
      for (const respondent of scorecardData.respondents) {
        initialRatings[ratingKey(statement.id, respondent)] = statement.ratings[respondent] ?? null;
      }
    }
    setRatings(initialRatings);
    setNarrative({
      strengths: scorecardData.summary.strengths || '',
      growthAreas: scorecardData.summary.growthAreas || '',
      recommendations: scorecardData.summary.recommendations || '',
    });
    hydratedForRef.current = assignmentId;
  }, [scorecardData, assignmentId]);

  const saveMutation = useMutation({
    mutationFn: (payload: ScorecardSavePayload) =>
      assessmentsService.saveScorecard(assessmentId, assignmentId, payload),
    onSuccess: () => {
      const stillDirty = dirtyRatingsRef.current.size || dirtyNarrativeRef.current.size;
      setSaveState(stillDirty ? 'pending' : 'saved');
      // Keeps the read-only summary on the Report tab and the report prefill in sync.
      queryClient.invalidateQueries({ queryKey: scorecardQueryKey(assessmentId, assignmentId) });
    },
    onError: () => setSaveState('error'),
  });

  const flush = useCallback(() => {
    const ratingKeys = Array.from(dirtyRatingsRef.current);
    const narrativeKeys = Array.from(dirtyNarrativeRef.current);
    if (!ratingKeys.length && !narrativeKeys.length) return;
    dirtyRatingsRef.current = new Set();
    dirtyNarrativeRef.current = new Set();

    const payload: ScorecardSavePayload = {};
    if (ratingKeys.length) {
      payload.ratings = ratingKeys.map((key) => {
        const [statementId, respondent] = key.split(':');
        return {
          statementId: parseInt(statementId, 10),
          respondent: respondent as ScorecardRespondent,
          rating: ratingsRef.current[key] ?? null,
        };
      });
    }
    for (const key of narrativeKeys) {
      payload[key] = narrativeRef.current[key] ?? '';
    }

    setSaveState('saving');
    saveMutation.mutate(payload);
  }, [saveMutation]);

  const scheduleFlush = useCallback(() => {
    setSaveState('pending');
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(flush, AUTOSAVE_DELAY_MS);
  }, [flush]);

  const handleRatingChange = useCallback((key: string, value: ScorecardRating | null) => {
    setRatings((prev) => ({ ...prev, [key]: value }));
    dirtyRatingsRef.current.add(key);
    scheduleFlush();
  }, [scheduleFlush]);

  const handleNarrativeChange = useCallback((key: keyof ScorecardNarrative, value: string) => {
    setNarrative((prev) => ({ ...prev, [key]: value }));
    dirtyNarrativeRef.current.add(key);
    scheduleFlush();
  }, [scheduleFlush]);

  // Save anything still pending when the assessor navigates away mid-sentence.
  useEffect(() => () => {
    if (timerRef.current) clearTimeout(timerRef.current);
    flush();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const respondents = scorecardData?.respondents || ['candidate'];
  const statements = scorecardData?.statements || [];
  const editable = canEdit && Boolean(scorecardData?.canEdit);

  const ratedCount = useMemo(
    () => Object.values(ratings).filter((rating) => Boolean(rating)).length,
    [ratings],
  );
  const totalCount = statements.length * respondents.length;
  const average = useMemo(() => {
    const values = Object.values(ratings).filter((rating): rating is ScorecardRating => Boolean(rating));
    if (!values.length) return null;
    return Math.round((values.reduce((sum, value) => sum + value, 0) / values.length) * 10) / 10;
  }, [ratings]);

  if (isLoading) {
    return <div className="text-sm text-gray-700">Loading scorecard…</div>;
  }

  if (isError) {
    return <div className="text-sm text-red-700">Failed to load the scorecard.</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">
            {scorecardData?.scorecard?.title || 'Scorecard'}
          </h2>
          <p className="mt-1 text-xs text-gray-500">
            Rate each statement, then summarize strengths, growth areas, and action steps. Saved automatically as you
            type.
          </p>
        </div>
        <div
          className={[
            'text-xs',
            saveState === 'error' ? 'text-red-700' : 'text-gray-500',
          ].join(' ')}
        >
          {saveState === 'error' ? 'Could not save scorecard' : saveStateLabel[saveState]}
        </div>
      </div>

      {!statements.length && (
        <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-700">
          No scorecard has been set up for this area yet. You can still write the summary below.
        </div>
      )}

      {statements.length > 0 && (
        <>
          <div className="flex flex-wrap items-center gap-4 rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-sm">
            <span className="text-gray-600">
              Rated <span className="font-medium text-gray-900">{ratedCount}</span> of {totalCount}
            </span>
            <span className="text-gray-600">
              Average{' '}
              <span className="font-medium text-gray-900">{average === null ? '—' : `${average.toFixed(1)}/4`}</span>
            </span>
          </div>

          <div className="space-y-4">
            {statements.map((statement, index) => (
              <div key={statement.id} className="space-y-2 rounded-lg border border-gray-200 px-3 py-3">
                <div className="text-sm text-gray-900">
                  <span className="mr-2 text-xs font-medium text-gray-400">{index + 1}.</span>
                  {statement.statement}
                </div>
                <div className="space-y-1">
                  {respondents.map((respondent) => (
                    <RatingRow
                      key={respondent}
                      name={`scorecard-${statement.id}-${respondent}`}
                      label={respondentLabel[respondent]}
                      showLabel={respondents.length > 1}
                      value={ratings[ratingKey(statement.id, respondent)] ?? null}
                      disabled={!editable}
                      onChange={(value) => handleRatingChange(ratingKey(statement.id, respondent), value)}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      <div className="space-y-4 border-t border-gray-200 pt-4">
        {narrativeFields.map((field) => (
          <div key={field.key} className="space-y-1">
            <div className="text-sm font-medium text-gray-900">{field.label}</div>
            <div className="text-xs text-gray-500">{field.help}</div>
            <AutoGrowTextarea
              value={narrative[field.key]}
              onChange={(value) => handleNarrativeChange(field.key, value)}
              disabled={!editable}
              placeholder={editable ? 'Write a few sentences' : 'Not provided'}
              minHeight={110}
            />
          </div>
        ))}
      </div>

      {onContinueToReport && (
        <div className="flex justify-end pt-2">
          <button
            type="button"
            className="px-4 py-2 rounded-md text-sm font-medium text-white bg-primary-600 hover:bg-primary-700"
            onClick={() => {
              flush();
              onContinueToReport();
            }}
          >
            Continue to Report
          </button>
        </div>
      )}
    </div>
  );
};

export const ScorecardSummary: React.FC<{
  scorecard?: ScorecardResponse;
  onInsert?: (text: string) => void;
}> = ({ scorecard, onInsert }) => {
  const average = useMemo(() => scorecardAverage(scorecard?.statements || []), [scorecard]);
  const narratives = useMemo(
    () => narrativeFields
      .map((field) => ({ ...field, body: scorecard?.summary?.[field.key]?.trim() || '' }))
      .filter((field) => field.body),
    [scorecard],
  );

  if (average === null && !narratives.length) return null;

  // The buttons live in the body rather than the summary row, because a click
  // inside <summary> would also toggle the panel shut.
  return (
    <details className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
      <summary className="cursor-pointer text-sm font-medium text-gray-900">
        Your scorecard{average === null ? '' : ` (${average.toFixed(1)}/4 average)`}
      </summary>
      <div className="mt-3 space-y-3">
        {onInsert && narratives.length > 0 && (
          <div className="flex justify-end">
            <button
              type="button"
              className="text-xs font-medium text-primary-600 hover:text-primary-700"
              onClick={() => onInsert(narratives.map(formatNarrativeForInsert).join('\n\n'))}
            >
              Insert all
            </button>
          </div>
        )}
        {narratives.map((field) => (
          <div key={field.key}>
            <div className="flex items-start justify-between gap-2">
              <div className="text-xs font-medium text-gray-600">{field.label}</div>
              {onInsert && (
                <button
                  type="button"
                  className="shrink-0 text-xs text-primary-600 hover:text-primary-700"
                  onClick={() => onInsert(formatNarrativeForInsert(field))}
                >
                  Insert
                </button>
              )}
            </div>
            <div className="mt-1 whitespace-pre-wrap text-sm text-gray-900">{field.body}</div>
          </div>
        ))}
      </div>
    </details>
  );
};
